/**
 * Side-by-side YAML diff view for "Review changes" modals (§4.4.2, §4.6).
 *
 * Uses a trivial line-based diff (LCS-equivalent for identical lines,
 * treating the rest as paired changes). That's good enough for the
 * small config snippets we're showing — we're not patching a kernel.
 *
 * For larger diffs we can swap in `diff` or `fast-diff` later.
 */
import { useMemo } from 'react';
import { cn } from '@/lib/utils';

interface YamlDiffViewProps {
  before: string;
  after: string;
  /** Height override; defaults to 60vh. */
  height?: string;
}

interface DiffLine {
  kind: 'eq' | 'add' | 'del' | 'sep';
  before?: string;
  after?: string;
  beforeNo?: number;
  afterNo?: number;
}

/**
 * Line-level LCS → produces a diff-like structure where matched lines
 * sit on the same row and mismatches stack (deletion on left, addition
 * on right).
 */
function computeDiff(a: string, b: string): DiffLine[] {
  const al = a.split('\n');
  const bl = b.split('\n');
  const m = al.length;
  const n = bl.length;

  // DP table for longest common subsequence.
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (al[i] === bl[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  let beforeNo = 1;
  let afterNo = 1;
  while (i < m && j < n) {
    if (al[i] === bl[j]) {
      out.push({ kind: 'eq', before: al[i], after: bl[j], beforeNo, afterNo });
      i++;
      j++;
      beforeNo++;
      afterNo++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ kind: 'del', before: al[i], beforeNo });
      i++;
      beforeNo++;
    } else {
      out.push({ kind: 'add', after: bl[j], afterNo });
      j++;
      afterNo++;
    }
  }
  while (i < m) {
    out.push({ kind: 'del', before: al[i], beforeNo });
    i++;
    beforeNo++;
  }
  while (j < n) {
    out.push({ kind: 'add', after: bl[j], afterNo });
    j++;
    afterNo++;
  }
  return out;
}

export function YamlDiffView({ before, after, height = '60vh' }: YamlDiffViewProps) {
  const diff = useMemo(() => computeDiff(before, after), [before, after]);
  const hasChanges = diff.some((d) => d.kind !== 'eq');

  return (
    <div
      className="border rounded-md overflow-auto font-mono text-xs bg-muted/30"
      style={{ maxHeight: height }}
      role="region"
      aria-label="YAML diff"
    >
      {!hasChanges && (
        <div className="p-6 text-center text-muted-foreground">
          No changes to review.
        </div>
      )}
      {hasChanges && (
        <table className="w-full border-collapse">
          <tbody>
            {diff.map((line, idx) => (
              <tr key={idx}>
                <td
                  className={cn(
                    'px-2 py-0.5 whitespace-pre align-top border-r w-1/2',
                    line.kind === 'del' && 'bg-destructive/10 text-destructive',
                    line.kind === 'eq' && 'text-muted-foreground'
                  )}
                >
                  <span className="inline-block w-8 text-right pr-2 select-none opacity-50">
                    {line.beforeNo ?? ''}
                  </span>
                  {line.kind === 'del' ? '- ' : line.kind === 'eq' ? '  ' : ''}
                  {line.before ?? ''}
                </td>
                <td
                  className={cn(
                    'px-2 py-0.5 whitespace-pre align-top w-1/2',
                    line.kind === 'add' && 'bg-success/10 text-success',
                    line.kind === 'eq' && 'text-muted-foreground'
                  )}
                >
                  <span className="inline-block w-8 text-right pr-2 select-none opacity-50">
                    {line.afterNo ?? ''}
                  </span>
                  {line.kind === 'add' ? '+ ' : line.kind === 'eq' ? '  ' : ''}
                  {line.after ?? ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Trivial YAML serializer for the small Agent objects we diff in v1. */
export function toYaml(value: unknown, indent = 0): string {
  const pad = '  '.repeat(indent);
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'string') {
    // Quote if it contains special chars or is empty.
    if (/[:#\n"'\\]/.test(value) || value === '' || /^\s/.test(value)) {
      return JSON.stringify(value);
    }
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    return value
      .map((v) => {
        if (typeof v === 'object' && v !== null) {
          return `${pad}-\n${toYaml(v, indent + 1)}`;
        }
        return `${pad}- ${toYaml(v, 0)}`;
      })
      .join('\n');
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return '{}';
    return entries
      .map(([k, v]) => {
        if (v === null || v === undefined) return `${pad}${k}: null`;
        if (typeof v === 'object') {
          return `${pad}${k}:\n${toYaml(v, indent + 1)}`;
        }
        return `${pad}${k}: ${toYaml(v, 0)}`;
      })
      .join('\n');
  }
  return String(value);
}
